import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  aadGroupMembers,
  getContainerPermissions,
  listAadGroups,
  listProfiles,
  setContainerRestriction,
  type AadGroupMembers,
  type AadGroupSummary,
  type ContainerPermissions,
  type ProfileSummary,
  type SetRestrictionInput,
  type SetRestrictionResult,
} from '@/services/api/endpoints';
import { addBreadcrumb } from '@/services/breadcrumbs/breadcrumbs';

// Hooks de la popup « Gérer la visibilité ». Tous les `enabled` sont explicites :
// la popup n'est montée qu'à l'ouverture, mais ses listes ne doivent pas être
// chargées avant d'être demandées. L'annuaire complet et les 381 groupes du
// tenant sont des appels loin d'être gratuits : le sélecteur ne les déclenche
// donc que lorsqu'un terme de recherche est saisi (cf. PrincipalPicker).

/** État courant des droits. À relire à chaque ouverture : quelqu'un d'autre a pu les changer. */
export function useContainerPermissions(containerId: string | null, enabled = true) {
  return useQuery<ContainerPermissions>({
    queryKey: ['container-permissions', containerId],
    queryFn: () => getContainerPermissions(containerId!),
    enabled: enabled && Boolean(containerId),
    // Volontairement court : la popup doit refléter l'état réel, pas un cache.
    staleTime: 0,
    retry: 1,
  });
}

/** Annuaire des collaborateurs actifs (~124 personnes) — filtrage serveur sur `q`. */
export function useProfiles(q: string, enabled = true) {
  return useQuery<ProfileSummary[]>({
    queryKey: ['profiles', q],
    queryFn: () => listProfiles(q),
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

/** Groupes Entra du tenant. `q` est un filtre de PRÉFIXE côté Graph (pas un « contient »). */
export function useAadGroups(q: string, enabled = true) {
  return useQuery<AadGroupSummary[]>({
    queryKey: ['aad-groups', q],
    queryFn: () => listAadGroups(q),
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

/**
 * Membres d'un groupe. L'appel déclenche une resynchronisation Entra côté
 * serveur — c'est le « rafraîchissement à l'ouverture de la popup » du modèle de
 * fraîcheur. On ne le met donc pas en cache longtemps, mais on évite de le
 * relancer à chaque re-render.
 */
export function useAadGroupMembers(groupId: string | null, enabled = true) {
  return useQuery<AadGroupMembers>({
    queryKey: ['aad-group-members', groupId],
    queryFn: () => aadGroupMembers(groupId!),
    enabled: enabled && Boolean(groupId),
    staleTime: 60 * 1000,
    retry: 1,
  });
}

export function useSetContainerRestriction() {
  const qc = useQueryClient();
  return useMutation<SetRestrictionResult, Error, SetRestrictionInput>({
    mutationFn: async (args) => {
      addBreadcrumb({
        category: 'mutation',
        message: 'set-container-restriction',
        // On journalise les COMPTEURS, pas les personnes : ces miettes partent
        // dans les rapports d'erreur.
        data: {
          containerId: args.containerId,
          isRestricted: args.isRestricted,
          principalCount: args.permissions.length,
        },
      });
      return setContainerRestriction(args);
    },
    onSuccess: (_res, args) => {
      // Un espace qui devient restreint peut disparaître de l'arbre, des
      // favoris ET des résultats de recherche pour d'autres utilisateurs — et
      // pour soi-même si l'on ne s'est pas inclus. On invalide donc tout ce qui
      // dérive de la visibilité, plus l'état des droits de cet espace.
      qc.invalidateQueries({ queryKey: ['containers'] });
      qc.invalidateQueries({ queryKey: ['favorites'] });
      qc.invalidateQueries({ queryKey: ['search'] });
      qc.invalidateQueries({ queryKey: ['container-permissions', args.containerId] });
    },
  });
}
